import React, { Component } from 'react';
import CognitoUtil from '../aws/cognito/cognitoUtil';
import LambdaUtil from '../aws/lambda/lambdaUtil';
import HistoryUtil from './historyUtil';
import { getCurrentLocale, strings } from '../../localization';
import {clearLocalAPIJWT} from '../../components/common/rcApi';
import Events from './events';
import PropTypes from 'prop-types';

export default class DeepLink extends Component {

    state = {
        invalidParams: false,
    }

    async componentDidMount() {

        const params = this.props.match.params;

        if(!this.validateParams(params)) {
            console.error('DeepLink: missing parameters', params);
            document.dispatchEvent(new CustomEvent(Events.HIDE_LOADING_PAGE));
            this.setState({ 
                invalidParams: true 
            });

            return;
        }

        try {

            document.dispatchEvent(Events.showLoading({ message: strings.deeplink_processing_your_request, override: true }));

            const stage = await this.refreshLogin();

            const accounts = await this.getUserAccounts(stage);

            const currentAccount = this.findCurrentAccount(accounts);

            if(!currentAccount) {
                throw new Error('DeepLink: account does not belong to user');
            }

            this.setCurrentAccount(currentAccount, accounts.length > 1);
            this.redirectToTarget();
        }
        catch(err) {
            return this.handleError(err);
        }
    }

    handleError = (error) => {
        const locale = getCurrentLocale();
        let redirectPath = window.location.pathname + window.location.search;
        redirectPath = redirectPath.replace(`/${locale}`, '');

        console.error('DeepLink: ', error);

        // This page tries to refresh the login, so if it fails, it means 
        // - the session has expired
        // - or the user is not logged in
        // - or wrong user has been logged in
        // In all cases, log out the user and redirect to login page
        // login page will use the redirect_to querystring to bring the user back to this page after successful login
        CognitoUtil.logout();
        window.location.href = `/${locale}?timeout=true&redirect_to=${encodeURIComponent(redirectPath)}`;
    }

    refreshLogin = () => {
        if(this.props.user) {
            return Promise.resolve(this.props.user.stage);
        }

        // I'm not using getUserData method from sessionManager because that includes steps that interfere with the flow here
        // To explain briefly: getUserData calls populateUserCurrentAccount method which redirects to selectAccount page if currentAccount is not valid or not set
        // And I can't set currentAccount at this step, because that breaks the getAccounts lambda query if logged in user is not associated with the account in the deeplink
        return new Promise((resolve, reject) => {
            CognitoUtil.refreshLogin(userAttributes => {
                const stage = this.getUserStage(userAttributes);

                if(!stage) {
                    return reject('DeepLink: user stage attribute not found', userAttributes);
                }

                resolve(stage);
            }, reject);
        });
    }

    getUserStage = (userAttributes) => {
        if (!Array.isArray(userAttributes)) return null;

        const attr = userAttributes.find(a =>
            (typeof a.getName === 'function' ? a.getName() : a.Name) === 'custom:linked_raven_stage'
        );

        if (!attr) return null;

        return (typeof attr.getValue === 'function') ? attr.getValue() : attr.Value;
    }

    getUserAccounts = (stage) => {

        if(this.props.accounts && this.props.accounts.length > 0) {
            return Promise.resolve(this.props.accounts);
        }

        // I'm not using getUserAccounts method from sessionManager because that includes steps that interfere with the flow here
        // To explain briefly: getUserAccounts reads stage from user.stage, but user is not available in sessionManager yet
        return new Promise((resolve, reject) => {
            LambdaUtil.getAccounts(stage, response => {
                const payload = JSON.parse(response.Payload);
                const accounts = payload.accounts.accounts;
                resolve(accounts);
            }, reject);
        });
    }

    findCurrentAccount = (accounts) => {
        const { accountId } = this.props.match.params;
        const decodedAccountId = decodeURIComponent(accountId);
        return accounts.find(account => account.accountId === decodedAccountId);
    }

    validateParams = ({ accountId, ravenId, cardId, eventId }) => {
        return !!(accountId && ravenId && cardId && eventId);
    }

    setCurrentAccount = (account, isMultiAccount) => {
        // Clear any existing API JWT (in case it belongs to a different account)
        clearLocalAPIJWT();
        
        CognitoUtil.setUserAccountsDetails(account, isMultiAccount);
    }

    redirectToTarget = () => {
        const { ravenId, cardId, eventId, lang } = this.props.match.params;

        // Redirect to the Event Summary page
        const url = `/stats/history/${ravenId}/${cardId}/${eventId}`;
        HistoryUtil.replace(url, lang);
    }

    render() {
        if(this.state.invalidParams) {
            return (
                <div id="deeplink" className='container'>
                    <div className='row'>
                        <div className='col'>
                            <div>{strings.deeplink_invalid_parameters}</div>                    
                        </div>
                    </div>
                </div>
            );
        }

        // this is a transient page, so render nothing
        return null;
    }
}

DeepLink.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            accountId: PropTypes.string.isRequired,
            ravenId: PropTypes.string.isRequired,
            cardId: PropTypes.string.isRequired,
            eventId: PropTypes.string.isRequired,
            lang: PropTypes.string,
        }).isRequired,
    }).isRequired,
    user: PropTypes.shape({
        stage: PropTypes.string,
    }),
    accounts: PropTypes.arrayOf(
        PropTypes.shape({
            accountId: PropTypes.string.isRequired,
        })
    ),
};

DeepLink.defaultProps = {
    user: null,
    accounts: null,
};